<template>
  <div>
    <div class="furniture" v-show="!furn.isEditing">
      <ProductImageCarousel
        :images="furn.shoppingListProductImages"
        :draggable="true"
        :editable="true"
        @on-add-images="
          onAddImages({
            shoppingListId,
            roomId: roomId,
            productId: furn.id,
          })
        "
        @on-remove-image="
          (image) =>
            onRemoveImage({
              shoppingListId: shoppingListId,
              roomId: roomId,
              productId: furn.id,
              image,
            })
        "
      />
      <div class="furniture__content">
        <div class="furniture__wrapper">
          <div class="furniture__base">
            <h4 class="furniture__title">{{ furn.name }}</h4>
            <div class="furniture__status-info">
              <span class="furniture__id">ID {{ furn.id }}</span>
              <span class="furniture__status" v-if="furn.status"
                >{{ $t("list.status_" + furn.status.toLowerCase()) }}
              </span>
              <a
                class="furniture__file"
                v-for="file in furn.shoppingListProductFiles"
                :key="file.id"
                :href="file.filePath"
                target="_blank"
              >
                <svg width="12" height="14" viewBox="0 0 12 14" fill="none">
                  <path
                    d="M10.0827 11.0833H3.66602C3.34518 11.0833 3.07063 10.9691 2.84235 10.7408C2.61368 10.5122 2.49935 10.2374 2.49935 9.91659V1.74992C2.49935 1.42909 2.61368 1.15434 2.84235 0.925669C3.07063 0.697391 3.34518 0.583252 3.66602 0.583252H7.2681C7.42365 0.583252 7.57202 0.612419 7.71318 0.670752C7.85396 0.729085 7.97782 0.811724 8.08477 0.918669L10.9139 3.74784C11.0209 3.85478 11.1035 3.97864 11.1618 4.11942C11.2202 4.26059 11.2493 4.40895 11.2493 4.5645V9.91659C11.2493 10.2374 11.1352 10.5122 10.9069 10.7408C10.6783 10.9691 10.4035 11.0833 10.0827 11.0833ZM7.16602 1.74992H3.66602V9.91659H10.0827V4.66659H7.74935C7.58407 4.66659 7.44563 4.61078 7.33402 4.49917C7.22202 4.38717 7.16602 4.24853 7.16602 4.08325V1.74992ZM1.33268 13.4166C1.01185 13.4166 0.737293 13.3024 0.509016 13.0742C0.280349 12.8455 0.166016 12.5708 0.166016 12.2499V4.66659C0.166016 4.50131 0.222016 4.36267 0.334016 4.25067C0.445627 4.13906 0.584071 4.08325 0.749349 4.08325C0.914627 4.08325 1.05327 4.13906 1.16527 4.25067C1.27688 4.36267 1.33268 4.50131 1.33268 4.66659V12.2499H7.16602C7.33129 12.2499 7.46993 12.3059 7.58193 12.4179C7.69354 12.5295 7.74935 12.668 7.74935 12.8333C7.74935 12.9985 7.69354 13.137 7.58193 13.2486C7.46993 13.3606 7.33129 13.4166 7.16602 13.4166H1.33268ZM3.66602 1.74992V4.66659V1.74992V9.91659V1.74992Z"
                    fill="#8C8F94"
                  />
                </svg>
                {{ file.name ?? $t("list.file_noname") }}
              </a>
            </div>
            <p class="furniture__producer">
              {{ $t("list.vendor") }} <span>{{ furn.vendor }}</span>
            </p>
            <p class="furniture__date">
              Termin realizacji:
              <span v-if="furn.completionDate">
                {{ moment(furn.completionDate).format("DD/MM/YYYY") }}
              </span>
            </p>
            <p class="furniture__description">
              {{ furn.description }}
            </p>
            <div class="furniture__favourites">
              <template v-if="projectInfo.myRoleInProject === Role.Client">
                <button
                  class="furniture__like-btn"
                  :class="{
                    'furniture__like-btn-active':
                      furn.customerAcceptanceStatus === 'LIKE',
                  }"
                  :title="$t('list.favourite_add')"
                  @click="
                    (furn.customerAcceptanceStatus !== 'LIKE' &&
                      onProductLike({
                        productId: furn.id,
                        roomId: roomId,
                      })) ||
                      (furn.customerAcceptanceStatus === 'LIKE' &&
                        onProductNone({
                          productId: furn.id,
                          roomId: roomId,
                        }))
                  "
                >
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="14" cy="14" r="13.5" stroke="#E4E6E4" />
                    <path
                      opacity="0.7"
                      d="M14.3129 22.4L11.8435 20.1833C10.9102 19.3407 10.1098 18.5889 9.44249 17.9278C8.77464 17.2667 8.22371 16.6444 7.78971 16.0611C7.35519 15.4778 7.0376 14.9139 6.83693 14.3694C6.63575 13.825 6.53516 13.2546 6.53516 12.6583C6.53516 11.4398 6.94349 10.4253 7.76016 9.61489C8.57682 8.80496 9.59442 8.4 10.8129 8.4C11.487 8.4 12.1287 8.54259 12.7379 8.82778C13.3472 9.11296 13.8722 9.51482 14.3129 10.0333C14.7537 9.51482 15.2787 9.11296 15.8879 8.82778C16.4972 8.54259 17.1389 8.4 17.8129 8.4C18.8629 8.4 19.7444 8.69478 20.4574 9.28433C21.1703 9.87441 21.6565 10.6167 21.9157 11.5111H20.2629C20.0296 10.9926 19.6861 10.6037 19.2324 10.3444C18.7787 10.0852 18.3055 9.95556 17.8129 9.95556C17.1518 9.95556 16.5815 10.1339 16.1018 10.4907C15.6222 10.8469 15.175 11.3167 14.7602 11.9H13.8657C13.4639 11.3167 13.007 10.8469 12.4953 10.4907C11.983 10.1339 11.4222 9.95556 10.8129 9.95556C10.074 9.95556 9.43549 10.2114 8.89727 10.7232C8.35956 11.2355 8.09071 11.8806 8.09071 12.6583C8.09071 13.0861 8.18145 13.5204 8.36293 13.9611C8.54442 14.4019 8.86849 14.9108 9.33516 15.4879C9.80182 16.0645 10.437 16.7383 11.2407 17.5093C12.0444 18.2809 13.0685 19.2111 14.3129 20.3C14.65 20.0019 15.0453 19.6583 15.499 19.2694C15.9528 18.8806 16.3157 18.5565 16.5879 18.2972L16.7629 18.4722L17.1417 18.8518L17.5213 19.2306L17.6963 19.4056C17.4111 19.6648 17.0481 19.9855 16.6074 20.3677C16.1666 20.7503 15.7778 21.0907 15.4407 21.3889L14.3129 22.4ZM19.7574 19.2889V16.9556H17.424V15.4H19.7574V13.0667H21.3129V15.4H23.6463V16.9556H21.3129V19.2889H19.7574Z"
                      fill="#A8ACAF"
                    />
                  </svg>
                </button>
                <button
                  class="furniture__dislike-btn"
                  :class="{
                    'furniture__dislike-btn-active':
                      furn.customerAcceptanceStatus === 'DISLIKE',
                  }"
                  :title="$t('list.favourite_remove')"
                  @click="
                    (furn.customerAcceptanceStatus !== 'DISLIKE' &&
                      onProductDislike({
                        productId: furn.id,
                        roomId: roomId,
                      })) ||
                      (furn.customerAcceptanceStatus === 'DISLIKE' &&
                        onProductNone({
                          productId: furn.id,
                          roomId: roomId,
                        }))
                  "
                >
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="14" cy="14" r="13.5" stroke="#E4E6E4" />
                    <path
                      d="M14.25 21.75L11.8687 19.6125C10.9688 18.8 10.197 18.075 9.5535 17.4375C8.9095 16.8 8.37825 16.2 7.95975 15.6375C7.54075 15.075 7.2345 14.5313 7.041 14.0063C6.847 13.4813 6.75 12.9313 6.75 12.3563C6.75 11.1813 7.14375 10.203 7.93125 9.42151C8.71875 8.64051 9.7 8.25001 10.875 8.25001C11.525 8.25001 12.1438 8.38751 12.7313 8.66251C13.3188 8.93751 13.825 9.32501 14.25 9.82501C14.675 9.32501 15.1812 8.93751 15.7687 8.66251C16.3562 8.38751 16.975 8.25001 17.625 8.25001C18.675 8.25001 19.6313 8.61876 20.4938 9.35626C21.3563 10.0938 21.7875 11.0938 21.7875 12.3563C21.7875 12.5313 21.775 12.7155 21.75 12.909C21.725 13.103 21.6875 13.3 21.6375 13.5H20.0438C20.1063 13.275 20.1562 13.0625 20.1937 12.8625C20.2312 12.6625 20.25 12.475 20.25 12.3C20.25 11.3625 19.9375 10.703 19.3125 10.3215C18.6875 9.94051 18.125 9.75001 17.625 9.75001C16.9875 9.75001 16.4375 9.92201 15.975 10.266C15.5125 10.6095 15.0813 11.0625 14.6812 11.625H13.8188C13.4313 11.0625 12.9908 10.6095 12.4973 10.266C12.0033 9.92201 11.4625 9.75001 10.875 9.75001C10.1625 9.75001 9.54675 9.99676 9.02775 10.4903C8.50925 10.9843 8.25 11.6063 8.25 12.3563C8.25 12.7688 8.3375 13.1875 8.5125 13.6125C8.6875 14.0375 9 14.5283 9.45 15.0848C9.9 15.6408 10.5125 16.2905 11.2875 17.034C12.0625 17.778 13.05 18.675 14.25 19.725C14.575 19.4375 14.9563 19.1063 15.3937 18.7313C15.8312 18.3563 16.1813 18.0438 16.4438 17.7938L16.6125 17.9625L16.9777 18.3285L17.3438 18.6938L17.5125 18.8625C17.2375 19.1125 16.8875 19.4218 16.4625 19.7903C16.0375 20.1593 15.6625 20.4875 15.3375 20.775L14.25 21.75ZM17.25 16.5V15H23.25V16.5H17.25Z"
                      fill="#A8ACAF"
                    />
                  </svg></button
              ></template>
              <template v-else>
                <div :title="$t('list.accepted')">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    :class="{
                      'furniture__like-icon-active':
                        furn.customerAcceptanceStatus === 'LIKE',
                    }"
                  >
                    <circle cx="14" cy="14" r="13.5" stroke="#E4E6E4" />
                    <path
                      d="M14.3129 22.4L11.8435 20.1833C10.9102 19.3407 10.1098 18.5889 9.44249 17.9278C8.77464 17.2667 8.22371 16.6444 7.78971 16.0611C7.35519 15.4778 7.0376 14.9139 6.83693 14.3694C6.63575 13.825 6.53516 13.2546 6.53516 12.6583C6.53516 11.4398 6.94349 10.4253 7.76016 9.61489C8.57682 8.80496 9.59442 8.4 10.8129 8.4C11.487 8.4 12.1287 8.54259 12.7379 8.82778C13.3472 9.11296 13.8722 9.51482 14.3129 10.0333C14.7537 9.51482 15.2787 9.11296 15.8879 8.82778C16.4972 8.54259 17.1389 8.4 17.8129 8.4C18.8629 8.4 19.7444 8.69478 20.4574 9.28433C21.1703 9.87441 21.6565 10.6167 21.9157 11.5111H20.2629C20.0296 10.9926 19.6861 10.6037 19.2324 10.3444C18.7787 10.0852 18.3055 9.95556 17.8129 9.95556C17.1518 9.95556 16.5815 10.1339 16.1018 10.4907C15.6222 10.8469 15.175 11.3167 14.7602 11.9H13.8657C13.4639 11.3167 13.007 10.8469 12.4953 10.4907C11.983 10.1339 11.4222 9.95556 10.8129 9.95556C10.074 9.95556 9.43549 10.2114 8.89727 10.7232C8.35956 11.2355 8.09071 11.8806 8.09071 12.6583C8.09071 13.0861 8.18145 13.5204 8.36293 13.9611C8.54442 14.4019 8.86849 14.9108 9.33516 15.4879C9.80182 16.0645 10.437 16.7383 11.2407 17.5093C12.0444 18.2809 13.0685 19.2111 14.3129 20.3C14.65 20.0019 15.0453 19.6583 15.499 19.2694C15.9528 18.8806 16.3157 18.5565 16.5879 18.2972L16.7629 18.4722L17.1417 18.8518L17.5213 19.2306L17.6963 19.4056C17.4111 19.6648 17.0481 19.9855 16.6074 20.3677C16.1666 20.7503 15.7778 21.0907 15.4407 21.3889L14.3129 22.4ZM19.7574 19.2889V16.9556H17.424V15.4H19.7574V13.0667H21.3129V15.4H23.6463V16.9556H21.3129V19.2889H19.7574Z"
                      fill="#A8ACAF"
                    />
                  </svg>
                </div>
                <div :title="$t('list.not_accepted')">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    :class="{
                      'furniture__dislike-icon-active':
                        furn.customerAcceptanceStatus === 'DISLIKE',
                    }"
                  >
                    <circle cx="14" cy="14" r="13.5" stroke="#E4E6E4" />
                    <path
                      d="M14.25 21.75L11.8687 19.6125C10.9688 18.8 10.197 18.075 9.5535 17.4375C8.9095 16.8 8.37825 16.2 7.95975 15.6375C7.54075 15.075 7.2345 14.5313 7.041 14.0063C6.847 13.4813 6.75 12.9313 6.75 12.3563C6.75 11.1813 7.14375 10.203 7.93125 9.42151C8.71875 8.64051 9.7 8.25001 10.875 8.25001C11.525 8.25001 12.1438 8.38751 12.7313 8.66251C13.3188 8.93751 13.825 9.32501 14.25 9.82501C14.675 9.32501 15.1812 8.93751 15.7687 8.66251C16.3562 8.38751 16.975 8.25001 17.625 8.25001C18.675 8.25001 19.6313 8.61876 20.4938 9.35626C21.3563 10.0938 21.7875 11.0938 21.7875 12.3563C21.7875 12.5313 21.775 12.7155 21.75 12.909C21.725 13.103 21.6875 13.3 21.6375 13.5H20.0438C20.1063 13.275 20.1562 13.0625 20.1937 12.8625C20.2312 12.6625 20.25 12.475 20.25 12.3C20.25 11.3625 19.9375 10.703 19.3125 10.3215C18.6875 9.94051 18.125 9.75001 17.625 9.75001C16.9875 9.75001 16.4375 9.92201 15.975 10.266C15.5125 10.6095 15.0813 11.0625 14.6812 11.625H13.8188C13.4313 11.0625 12.9908 10.6095 12.4973 10.266C12.0033 9.92201 11.4625 9.75001 10.875 9.75001C10.1625 9.75001 9.54675 9.99676 9.02775 10.4903C8.50925 10.9843 8.25 11.6063 8.25 12.3563C8.25 12.7688 8.3375 13.1875 8.5125 13.6125C8.6875 14.0375 9 14.5283 9.45 15.0848C9.9 15.6408 10.5125 16.2905 11.2875 17.034C12.0625 17.778 13.05 18.675 14.25 19.725C14.575 19.4375 14.9563 19.1063 15.3937 18.7313C15.8312 18.3563 16.1813 18.0438 16.4438 17.7938L16.6125 17.9625L16.9777 18.3285L17.3438 18.6938L17.5125 18.8625C17.2375 19.1125 16.8875 19.4218 16.4625 19.7903C16.0375 20.1593 15.6625 20.4875 15.3375 20.775L14.25 21.75ZM17.25 16.5V15H23.25V16.5H17.25Z"
                      fill="#A8ACAF"
                    />
                  </svg>
                </div>
              </template>
              <a
                v-if="furn.link"
                class="furniture__link"
                :href="
                  furn.link.startsWith('http') ? furn.link : '//' + furn.link
                "
                target="_blank"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 13 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.50841 7.4916C5.79674 7.76587 5.79674 8.21596 5.50841 8.49023C5.23414 8.7645 4.78405 8.7645 4.50978 8.49023C3.85109 7.83055 3.48113 6.93643 3.48113 6.00421C3.48113 5.07199 3.85109 4.17787 4.50978 3.51819L6.99931 1.02866C7.65899 0.369971 8.55311 7.62939e-06 9.48533 7.62939e-06C10.4176 7.62939e-06 11.3117 0.369971 11.9714 1.02866C12.63 1.68833 13 2.58245 13 3.51468C13 4.4469 12.63 5.34102 11.9714 6.00069L10.9235 7.04855C10.9305 6.47188 10.8391 5.89521 10.6422 5.34666L10.9727 5.0091C11.1699 4.81408 11.3264 4.58189 11.4332 4.32598C11.54 4.07006 11.595 3.7955 11.595 3.51819C11.595 3.24088 11.54 2.96632 11.4332 2.71041C11.3264 2.45449 11.1699 2.22231 10.9727 2.02728C10.7777 1.83013 10.5455 1.67362 10.2896 1.5668C10.0337 1.45999 9.75913 1.40499 9.48182 1.40499C9.2045 1.40499 8.92995 1.45999 8.67403 1.5668C8.41812 1.67362 8.18593 1.83013 7.99091 2.02728L5.50841 4.50979C5.31126 4.70481 5.15474 4.937 5.04793 5.19291C4.94111 5.44882 4.88611 5.72338 4.88611 6.00069C4.88611 6.27801 4.94111 6.55256 5.04793 6.80848C5.15474 7.06439 5.31126 7.29658 5.50841 7.4916ZM7.49159 4.50979C7.76586 4.23552 8.21595 4.23552 8.49022 4.50979C9.14891 5.16946 9.51887 6.06358 9.51887 6.9958C9.51887 7.92803 9.14891 8.82215 8.49022 9.48182L6.00069 11.9714C5.34101 12.63 4.44689 13 3.51467 13C2.58245 13 1.68833 12.63 1.02865 11.9714C0.369963 11.3117 0 10.4176 0 9.48534C0 8.55312 0.369963 7.659 1.02865 6.99932L2.07651 5.95147C2.06947 6.52814 2.1609 7.10481 2.35781 7.66038L2.02728 7.99092C1.83013 8.18594 1.67361 8.41813 1.5668 8.67404C1.45998 8.92995 1.40498 9.20451 1.40498 9.48182C1.40498 9.75914 1.45998 10.0337 1.5668 10.2896C1.67361 10.5455 1.83013 10.7777 2.02728 10.9727C2.2223 11.1699 2.45449 11.3264 2.7104 11.4332C2.96631 11.54 3.24087 11.595 3.51818 11.595C3.7955 11.595 4.07006 11.54 4.32597 11.4332C4.58188 11.3264 4.81407 11.1699 5.00909 10.9727L7.49159 8.49023C7.68874 8.29521 7.84526 8.06302 7.95208 7.80711C8.05889 7.55119 8.11389 7.27663 8.11389 6.99932C8.11389 6.72201 8.05889 6.44745 7.95208 6.19154C7.84526 5.93562 7.68874 5.70344 7.49159 5.50841C7.42329 5.44441 7.36885 5.36708 7.33163 5.28119C7.29441 5.19531 7.27521 5.1027 7.27521 5.0091C7.27521 4.9155 7.29441 4.82289 7.33163 4.73701C7.36885 4.65112 7.42329 4.57379 7.49159 4.50979Z"
                    fill="#468CDE"
                  />
                </svg>
                {{ furn.link }}
              </a>
            </div>
          </div>
          <div class="furniture__info">
            <span>{{ $t("list.amount") }}</span>
            <span>{{ furn.amount }}</span>
            <span>{{ $t("list.price_unit") }}</span>
            <span>{{ currency(furn.price, furn.currency) }}</span>
            <span>{{ $t("list.sum") }}</span>
            <span>{{ currency(furn.amount * furn.price, furn.currency) }}</span>
          </div>
        </div>
        <div class="furniture__actions">
          <button
            v-if="projectInfo.myRoleInProject === Role.Architect"
            class="furniture__actions-button"
            :title="$t('list.product_open_menu')"
            v-focus-click
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.0025 7.41957C12.4019 7.41957 12.7257 7.09578 12.7257 6.69636C12.7257 6.29694 12.4019 5.97314 12.0025 5.97314C11.6031 5.97314 11.2793 6.29694 11.2793 6.69636C11.2793 7.09578 11.6031 7.41957 12.0025 7.41957Z"
                stroke="#498388"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M12.0025 12.7233C12.4019 12.7233 12.7257 12.3995 12.7257 12.0001C12.7257 11.6006 12.4019 11.2769 12.0025 11.2769C11.6031 11.2769 11.2793 11.6006 11.2793 12.0001C11.2793 12.3995 11.6031 12.7233 12.0025 12.7233Z"
                stroke="#498388"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M12.0025 18.0265C12.4019 18.0265 12.7257 17.7027 12.7257 17.3033C12.7257 16.9039 12.4019 16.5801 12.0025 16.5801C11.6031 16.5801 11.2793 16.9039 11.2793 17.3033C11.2793 17.7027 11.6031 18.0265 12.0025 18.0265Z"
                stroke="#498388"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <circle cx="12" cy="12" r="11.5" stroke="#B9CFD1" />
            </svg>
            <div class="action-menu">
              <ul>
                <li @click="onEditProduct(furn.id, furn)" tabindex="0">
                  <img src="@/assets/icons/settings.svg" alt="Edytuj produkt" />
                  {{ $t("list.product_edit") }}
                </li>
                <li @click="onCopyProduct(furn)" tabindex="0">
                  <img src="@/assets/icons/copy-v2.svg" alt="Kopiuj produkt" />
                  {{ $t("list.product_copy") }}
                </li>

                <li @click="emit('onDeleteProduct')" tabindex="0">
                  <img src="@/assets/icons/delete-v2.svg" alt="Usuń produkt" />
                  {{ $t("list.product_delete") }}
                </li>
              </ul>
            </div>
          </button>
          <div v-else></div>
          <button
            class="furniture__actions-button"
            :title="$t('list.comments_open')"
            @click="store.openComments(furn.id)"
          >
            <svg
              width="18"
              height="16"
              viewBox="0 0 18 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="furniture__comment-icon"
              :class="{
                'furniture__comment-icon-active': furn.numberOfComments > 0,
              }"
            >
              <path
                d="M5.06418 6.1867C4.44199 6.1867 3.93932 6.68937 3.93932 7.31155C3.93932 7.93374 4.44199 8.43641 5.06418 8.43641C5.68636 8.43641 6.18903 7.93374 6.18903 7.31155C6.18903 6.68937 5.68636 6.1867 5.06418 6.1867ZM9.00117 6.1867C8.37898 6.1867 7.87631 6.68937 7.87631 7.31155C7.87631 7.93374 8.37898 8.43641 9.00117 8.43641C9.62335 8.43641 10.126 7.93374 10.126 7.31155C10.126 6.68937 9.62335 6.1867 9.00117 6.1867ZM12.9382 6.1867C12.316 6.1867 11.8133 6.68937 11.8133 7.31155C11.8133 7.93374 12.316 8.43641 12.9382 8.43641C13.5603 8.43641 14.063 7.93374 14.063 7.31155C14.063 6.68937 13.5603 6.1867 12.9382 6.1867ZM9.00117 0C4.03072 0 0.00233296 3.27262 0.00233296 7.31155C0.00233296 8.98477 0.701852 10.5174 1.86186 11.7512C1.3381 13.1362 0.248395 14.3103 0.230819 14.3243C-0.00118221 14.5704 -0.0644553 14.9289 0.0691212 15.2383C0.202698 15.5476 0.508517 15.748 0.845974 15.748C3.0078 15.748 4.71266 14.8446 5.73558 14.1204C6.75146 14.4403 7.84819 14.6231 9.00117 14.6231C13.9716 14.6231 18 11.3505 18 7.31155C18 3.27262 13.9716 0 9.00117 0ZM9.00117 12.9358C8.06262 12.9358 7.13461 12.7917 6.24527 12.5105L5.44733 12.2574L4.76187 12.7425C4.2592 13.0975 3.57023 13.4947 2.74065 13.7619C2.99726 13.3366 3.24683 12.8585 3.44017 12.3488L3.81278 11.361L3.08865 10.5947C2.45241 9.9163 1.68961 8.79496 1.68961 7.31155C1.68961 4.21117 4.96927 1.68728 9.00117 1.68728C13.0331 1.68728 16.3127 4.21117 16.3127 7.31155C16.3127 10.4119 13.0331 12.9358 9.00117 12.9358Z"
                fill="currentColor"
              />
            </svg>
            <div>
              <span
                v-if="
                  !furn.isLastMessageReadByUser && furn.numberOfComments > 0
                "
                class="furniture__comment-dot"
              ></span>
              <span
                v-if="furn.numberOfComments > 0"
                class="furniture__comment-count"
              >
                {{ furn.numberOfComments }}
              </span>
            </div>
          </button>
        </div>
      </div>
      <ShoppingListComments
        :project-id="projectId"
        :shopping-list-id="shoppingListId"
        :room-id="roomId"
        :product-id="furn.id"
        v-if="furn.commentsOpen"
        @on-close="furn.commentsOpen = false"
      />
    </div>
    <ShoppingListProductForm
      v-show="furn.isEditing"
      :project-id="projectId"
      :shopping-list-id="shoppingListId"
      :room-id="roomId"
      ref="productForm"
      @close="furn.isEditing = false"
    />
    <input
      hidden
      type="file"
      @change="onProductImagesPicked"
      ref="productImageInput"
    />
  </div>
</template>

<script setup>
import ShoppingListComments from "./ShoppingListComments.vue";
import ProductImageCarousel from "./ProductImageCarousel.vue";
import ShoppingListProductForm from "./ShoppingListProductForm.vue";
import { currency } from "@/helpsers/currency.helper";
import { useShoppingListStore } from "@/stores/shoppingList.store";
import { ref } from "vue";
import { Role } from "@/helpsers/role.helper";
import { useAuthStore } from "@/stores/auth.store";
import { vFocusClick } from "@/helpsers/focus-click.directive";
import moment from "moment";

const props = defineProps({
  projectId: {},
  projectInfo: {},
  shoppingListId: {},
  roomId: {},
  furn: {},
});
const emit = defineEmits(["onDeleteProduct", "onCopyProduct"]);

const auth = useAuthStore();
const store = useShoppingListStore();

const productForm = ref(null);
function onEditProduct(productId, data) {
  const canEdit = store.canEditOrAddProduct();
  if (!canEdit) {
    return;
  }
  props.furn.isEditing = true;
  productForm.value.showEditProduct({
    productId,
    data,
  });
}
function onCopyProduct(data) {
  const canAdd = store.canEditOrAddProduct();
  if (!canAdd) {
    return;
  }
  emit('onCopyProduct', data)
}
function onProductLike({ productId, roomId }) {
  store.likeProduct({
    shoppingListId: props.shoppingListId,
    productId,
    roomId,
  });
}
function onProductDislike({ productId, roomId }) {
  store.dislikeProduct({
    shoppingListId: props.shoppingListId,
    productId,
    roomId,
  });
}

function onProductNone({ productId, roomId }) {
  store.noneLikeProduct({
    shoppingListId: props.shoppingListId,
    productId,
    roomId,
  });
}

const productImageInput = ref();
let tempAddImageData = null;
function onAddImages({ shoppingListId, roomId, productId }) {
  tempAddImageData = {
    shoppingListId,
    roomId,
    productId,
  };
  productImageInput.value?.click();
}
async function onProductImagesPicked(event) {
  const eventFiles = event.target.files;
  const files = Array.from(eventFiles);
  await store.addImages({
    shoppingListId: tempAddImageData.shoppingListId,
    roomId: tempAddImageData.roomId,
    productId: tempAddImageData.productId,
    imageFilesToSend: files,
  });
  tempAddImageData = null;
}
async function onRemoveImage({ shoppingListId, roomId, productId, image }) {
  const imageId = image.id;
  await store.removeImage({
    shoppingListId,
    roomId,
    productId,
    imageId,
  });
}
</script>

<style lang="scss" scoped>
@use "@/css/shopping-list.scss";
@use "@/css/shopping-list-product.scss";
</style>
